import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { usePage, Link, router, useForm } from '@inertiajs/react'
import TypeBadge from '../../components/TypeBadge'
import Logo from '../../components/Logo'
import { RELATION_COLORS } from '../../components/MemoryGraph'

const ARCHIVED_BY_LABELS = {
  manual:           "Manual",
  decay:            "Decaimiento",
  stale_synthesis:  "Síntesis obsoleta",
  expiry:           "TTL expirado",
}

export default function Show({ memory, back_url, related, relations = [] }) {
  const { auth } = usePage().props
  const isOwner = auth?.user?.role === 'owner'
  const date = new Date(memory.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric"
  })
  const [confirming, setConfirming] = useState(false)
  const [showRelationForm, setShowRelationForm] = useState(false)
  const btnRef = useRef(null)

  useEffect(() => {
    if (!confirming) return
    const handler = (e) => { if (!btnRef.current?.contains(e.target)) setConfirming(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [confirming])

  function handleForget() {
    if (!confirming) { setConfirming(true); return }
    router.patch(`/memories/${memory.id}/archive`, {}, {
      onSuccess: () => router.visit(back_url ?? '/')
    })
  }

  const hasRelated = related &&
    (related.session.length > 0 || related.namespace.length > 0 || related.project.length > 0)

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-700 px-6 py-3 flex items-center justify-between shrink-0 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-800/30">
        <div className="flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2.5 no-underline group">
            <Logo variant="mark" size={28} />
            <span className="text-base font-mono font-bold tracking-[0.12em] uppercase text-zinc-100 group-hover:text-emerald-300 transition-colors">
              anamnesis
            </span>
          </Link>
          <a
            href={back_url}
            onClick={e => { e.preventDefault(); window.history.back() }}
            className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-400 rounded-md px-2.5 py-1 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
          >
            <span className="text-zinc-300">‹</span> back
          </a>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <a
              href={`/memories/${memory.id}/edit`}
              className="text-[11px] font-mono text-emerald-400/80 hover:text-emerald-300 border border-emerald-800/60 hover:border-emerald-700/80 rounded-md px-3 py-1 bg-emerald-950/30 hover:bg-emerald-950/50 transition-colors"
            >
              edit
            </a>
            {!memory.archived_at && (
              <button
                ref={btnRef}
                onClick={handleForget}
                className={`text-[11px] font-mono rounded-md px-3 py-1 transition-colors border ${
                  confirming
                    ? "text-red-400 border-red-800/80 bg-red-950/40 hover:bg-red-950/60"
                    : "text-zinc-500 border-zinc-700 hover:text-zinc-300 hover:border-zinc-500 bg-zinc-800/30 hover:bg-zinc-800/60"
                }`}
              >
                {confirming ? "sure?" : "forget"}
              </button>
            )}
          </div>
        )}
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-6 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TypeBadge type={memory.memory_type} />
              {memory.memory_type === 'synthesis' && memory.synthesis_depth != null && (
                <span className="inline-block px-1.5 py-0.5 rounded text-xs font-mono bg-orange-950/60 text-orange-400/70 border border-orange-900/40">
                  depth {memory.synthesis_depth}
                </span>
              )}
              <span className="text-xs font-mono text-zinc-500">{memory.scope}</span>
              {memory.archived_at && (
                <>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono bg-zinc-800 text-zinc-500 border border-zinc-700">
                    archived
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">
                    {ARCHIVED_BY_LABELS[memory.archived_by] ?? "–"}
                  </span>
                </>
              )}
              {memory.topic_key && (
                <span className="text-xs font-mono text-zinc-500 bg-zinc-800/60 border border-zinc-700/50 px-2 py-0.5 rounded-md">
                  {memory.topic_key}
                </span>
              )}
              <span className="text-xs font-mono text-zinc-500 ml-auto">{date}</span>
            </div>

            {/* Title */}
            <h1 className="text-xl font-mono font-bold text-zinc-100 mb-6 leading-snug">
              {memory.title}
            </h1>

            {/* Content */}
            <div className="markdown-body font-mono text-sm text-zinc-300 leading-relaxed space-y-3">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-lg font-bold text-zinc-100 mt-4 mb-2">{children}</h1>,
                  h2: ({children}) => <h2 className="text-base font-bold text-zinc-100 mt-4 mb-2">{children}</h2>,
                  h3: ({children}) => <h3 className="text-sm font-bold text-zinc-200 mt-3 mb-1">{children}</h3>,
                  p:  ({children}) => <p className="text-zinc-300">{children}</p>,
                  strong: ({children}) => <strong className="text-zinc-100 font-semibold">{children}</strong>,
                  em: ({children}) => <em className="text-zinc-400 italic">{children}</em>,
                  code: ({inline, children}) => inline
                    ? <code className="text-emerald-400 bg-zinc-800 px-1 py-0.5 rounded text-xs">{children}</code>
                    : <code className="block bg-zinc-800 border border-zinc-700 rounded p-3 text-xs text-emerald-400 overflow-x-auto">{children}</code>,
                  pre: ({children}) => <pre className="bg-zinc-800 border border-zinc-700 rounded p-3 overflow-x-auto">{children}</pre>,
                  ul: ({children}) => <ul className="list-disc list-inside space-y-1 text-zinc-300 pl-2">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside space-y-1 text-zinc-300 pl-2">{children}</ol>,
                  li: ({children}) => <li className="text-zinc-300">{children}</li>,
                  a:  ({href, children}) => <a href={href} className="text-emerald-400 hover:underline" target="_blank" rel="noreferrer">{children}</a>,
                  hr: () => <hr className="border-zinc-700 my-4" />,
                  blockquote: ({children}) => <blockquote className="border-l-2 border-zinc-600 pl-4 text-zinc-400 italic">{children}</blockquote>,
                }}
              >
                {memory.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Related panel */}
          {hasRelated && <RelatedPanel related={related} />}
        </div>

        {/* Relations section */}
        <div className="mt-8 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Relations
            </p>
            <div className="flex items-center gap-2">
              <a
                href={`/memories/${memory.id}/graph`}
                className="text-[10px] font-mono text-emerald-400/70 hover:text-emerald-300 border border-emerald-900/40 hover:border-emerald-700/60 rounded-md px-2 py-0.5 bg-emerald-950/20 hover:bg-emerald-950/40 transition-colors"
              >
                View graph
              </a>
              {isOwner && !memory.archived_at && (
                <button
                  onClick={() => setShowRelationForm(true)}
                  className="text-xs font-mono text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-lg px-2 py-0.5 transition-colors"
                >
                  + add relation
                </button>
              )}
            </div>
          </div>
          {relations.length > 0 ? (
            <div className="space-y-2">
              {relations.map(rel => (
                <div key={rel.id} className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-zinc-500 w-4 shrink-0">
                    {rel.direction === "outgoing" ? "→" : "←"}
                  </span>
                  <RelationTypePill type={rel.relation_type} />
                  <Link
                    href={`/memories/${rel.other_memory.id}`}
                    className="text-zinc-300 hover:text-white truncate transition-colors"
                  >
                    {rel.other_memory.title}
                  </Link>
                  <span className="shrink-0">
                    <TypeBadge type={rel.other_memory.memory_type} />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs font-mono text-zinc-600">No relations yet.</p>
          )}
        </div>

        {/* Add Relation modal */}
        {showRelationForm && (
          <AddRelationModal
            memoryId={memory.id}
            onClose={() => setShowRelationForm(false)}
          />
        )}
      </div>
    </div>
  )
}

function RelatedPanel({ related }) {
  return (
    <aside className="w-64 shrink-0 space-y-4">
      {related.session.length > 0 && (
        <RelatedSection
          title="Same session"
          items={related.session}
          accent="violet"
        />
      )}
      {related.namespace.length > 0 && (
        <RelatedSection
          title="Same namespace"
          items={related.namespace}
          accent="emerald"
        />
      )}
      {related.project.length > 0 && (
        <RelatedSection
          title="From this project"
          items={related.project}
          accent="zinc"
        />
      )}
    </aside>
  )
}

function RelationTypePill({ type }) {
  // Consume RELATION_COLORS from MemoryGraph — single source of truth (DECISION-5)
  const color = RELATION_COLORS[type]
  const pillBg   = color?.pillBg   ?? 'bg-zinc-800'
  const pillText = color?.pillText ?? 'text-zinc-400'
  const border   = pillBg.includes('amber')   ? 'border-amber-900/40'   :
                   pillBg.includes('red')     ? 'border-red-900/40'     :
                   pillBg.includes('orange')  ? 'border-orange-900/40'  :
                   pillBg.includes('sky')     ? 'border-sky-900/40'     :
                   pillBg.includes('emerald') ? 'border-emerald-900/40' :
                   pillBg.includes('teal')    ? 'border-teal-900/40'    :
                   pillBg.includes('violet')  ? 'border-violet-900/40'  :
                   'border-zinc-700'
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono border shrink-0 ${pillBg} ${pillText} ${border}`}>
      {type}
    </span>
  )
}

function RelatedSection({ title, items, accent }) {
  const accentMap = {
    violet: "text-violet-400 border-violet-800/40",
    emerald: "text-emerald-400 border-emerald-800/40",
    zinc:    "text-zinc-500 border-zinc-800",
  }
  return (
    <div className={`border rounded p-3 space-y-2 ${accentMap[accent].split(' ')[1]}`}>
      <p className={`text-[10px] font-mono uppercase tracking-widest ${accentMap[accent].split(' ')[0]}`}>
        {title}
      </p>
      {items.map(m => (
        <a
          key={m.id}
          href={`/memories/${m.id}`}
          className="block group"
        >
          <p className="text-xs font-mono text-zinc-300 group-hover:text-white truncate transition-colors leading-tight">
            {m.title}
          </p>
          <p className="text-[10px] font-mono text-zinc-600 mt-0.5">{m.memory_type}</p>
        </a>
      ))}
    </div>
  )
}

const RELATION_TYPES = [
  'supersedes',
  'synthesized_from',
  'conflicts_with',
  'related',
  'compatible',
  'scoped',
  'not_conflict',
]

function AddRelationModal({ memoryId, onClose }) {
  const { data, setData, post, processing, errors } = useForm({
    target_id:     '',
    relation_type: RELATION_TYPES[0],
  })

  function handleSubmit(e) {
    e.preventDefault()
    post(`/memories/${memoryId}/adjudicate`, {
      onSuccess: () => onClose(),
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-mono font-bold text-zinc-100">Add Relation</h2>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 transition-colors text-sm font-mono"
          >
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Target Memory ID
            </label>
            <input
              type="number"
              value={data.target_id}
              onChange={e => setData('target_id', e.target.value)}
              required
              min="1"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
              placeholder="e.g. 42"
            />
            {errors.target_id && (
              <p className="mt-1 text-[10px] font-mono text-red-400">{errors.target_id}</p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Relation Type
            </label>
            <select
              value={data.relation_type}
              onChange={e => setData('relation_type', e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
            >
              {RELATION_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-mono text-zinc-400 hover:text-zinc-200 border border-zinc-700 hover:border-zinc-500 rounded-lg px-3 py-1.5 transition-colors"
            >
              cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="text-xs font-mono text-emerald-300 hover:text-emerald-200 border border-emerald-800/60 bg-emerald-900/30 hover:bg-emerald-800/40 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {processing ? 'saving...' : 'add relation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
